/**
 * A single inline icon set. Icons are 16×16 line drawings on a 24-unit grid so they sit on the
 * same optical weight as the type; decorative ones are hidden from assistive technology and
 * every icon-only control carries its own label.
 */

const PATHS = {
  check: "M20 6 9 17l-5-5",
  circle: "M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18",
  "circle-dot": "M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18M12 9.5a2.5 2.5 0 1 0 0 5 2.5 2.5 0 0 0 0-5",
  "check-circle": "M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18M8 12.5l2.5 2.5L16 9.5",
  cross: "M18 6 6 18M6 6l12 12",
  "cross-circle": "M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18M9 9l6 6M15 9l-6 6",
  warning: "M12 4 2.5 20h19L12 4ZM12 10v5M12 17.4v.2",
  arrowLeft: "M19 12H5M11 6l-6 6 6 6",
  arrowRight: "M5 12h14M13 6l6 6-6 6",
  chevronRight: "M9 6l6 6-6 6",
  chevronDown: "M6 9l6 6 6-6",
  copy: "M9 9h10v10H9zM5 15V5h10",
  download: "M12 4v11M7 11l5 5 5-5M4 20h16",
  play: "M7 4.5 19 12 7 19.5z",
  stop: "M6 6h12v12H6z",
  refresh: "M20 12a8 8 0 1 1-2.7-6M20 4v5h-5",
  terminal: "M4 5h16v14H4zM7.5 10l2.5 2.5-2.5 2.5M12.5 15.5H16",
  file: "M6 3h7l5 5v13H6zM13 3v5h5",
  folder: "M3 6h6l2 2.5h10V20H3z",
  search: "M11 4a7 7 0 1 0 0 14 7 7 0 0 0 0-14M16.2 16.2 21 21",
  sun: "M12 7.5a4.5 4.5 0 1 0 0 9 4.5 4.5 0 0 0 0-9M12 2v2M12 20v2M4.2 4.2l1.4 1.4M18.4 18.4l1.4 1.4M2 12h2M20 12h2M4.2 19.8l1.4-1.4M18.4 5.6l1.4-1.4",
  moon: "M20 14.5A8.5 8.5 0 0 1 9.5 4a8.5 8.5 0 1 0 10.5 10.5",
  monitor: "M3 5h18v11H3zM9 20h6M12 16v4",
  trash: "M4 7h16M9 7V4h6v3M6 7l1 13h10l1-13",
  lock: "M6 11h12v9H6zM9 11V8a3 3 0 0 1 6 0v3",
  key: "M15.5 3a5.5 5.5 0 1 0-4.1 9.2L4 19.6V21h3v-2h2v-2h2l1.4-1.4A5.5 5.5 0 0 0 15.5 3",
  user: "M12 4a4 4 0 1 0 0 8 4 4 0 0 0 0-8M4 21v-1.5A4.5 4.5 0 0 1 8.5 15h7a4.5 4.5 0 0 1 4.5 4.5V21",
  save: "M5 4h11l3 3v13H5zM8 4v6h8V4M8 20v-6h8v6",
  bulb: "M9 18h6M10 21h4M12 3a6 6 0 0 0-3.5 10.9V18h7v-4.1A6 6 0 0 0 12 3",
  flag: "M6 3v18M6 4h12l-2.5 4.5L18 13H6",
  external: "M14 4h6v6M20 4l-9 9M18 14v6H4V6h6",
  plus: "M12 5v14M5 12h14",
  shield: "M12 3 5 6v6c0 4 3 7.5 7 9 4-1.5 7-5 7-9V6z",
  book: "M4 4h7v16H4zM13 4h7v16h-7M11 4c0 2 2 2 2 0",
  award: "M12 3a5.5 5.5 0 1 0 0 11 5.5 5.5 0 0 0 0-11M9 13.8 7.5 21l4.5-2.5L16.5 21 15 13.8",
  chart: "M4 20V9M10 20V4M16 20v-7M22 20H3",
} as const;

export type IconName = keyof typeof PATHS;

export function Icon({
  name,
  size = 16,
  label,
  className,
}: {
  name: IconName;
  size?: number;
  /** Supply when the icon is the only thing conveying the meaning. */
  label?: string;
  className?: string;
}) {
  return (
    <svg
      className={className}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill={name === "play" || name === "stop" ? "currentColor" : "none"}
      stroke="currentColor"
      strokeWidth={name === "play" || name === "stop" ? 0 : 1.7}
      strokeLinecap="square"
      strokeLinejoin="miter"
      role={label ? "img" : undefined}
      aria-label={label}
      aria-hidden={label ? undefined : true}
      focusable="false"
    >
      <path d={PATHS[name]} />
    </svg>
  );
}

/** The brand mark: three contour lines and a marker, matching the favicon. */
export function BrandGlyph({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 32 32" aria-hidden="true" focusable="false">
      <rect width="32" height="32" rx="3" fill="currentColor" opacity="0.08" />
      <g fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="square">
        <path d="M7 11h8" />
        <path d="M7 16h12" />
        <path d="M7 21h6" />
      </g>
      <circle cx="24" cy="21" r="2.4" fill="var(--moss)" />
    </svg>
  );
}
